import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { RevealCard, DayFixture } from '@/types/reveal';
import { setStoredTodaysPick, clearStoredTodaysPick } from '@/types/reveal';
import { dailyRevealKeys, findFixtureForCard, formatFixtureTime } from '@/hooks/useDailyReveal';
import { isPropPickLocked } from '@/utils/propBasedDailyReveal';
import { PropCardInfo } from '@/components/reveal/PropCardInfo';
import { MlbTeamLogoImage } from '@/components/app-entry/MlbTeamLogoImage';
import { MlbTeamMarkImage } from '@/components/app-entry/MlbTeamMarkImage';
import { toTeamAbbreviation } from '@/utils/teamAbbreviation';
import { useAudioSettings } from '@/contexts/AudioSettingsContext';
import { getDailyRevealConfig } from '@low6-pickem/sports-config';
import { getJerseyAvatarUrl } from '@/utils/jerseyAvatar';
import { FitText } from '@/components/common/FitText';

const FIXTURE_LOCKED_STATUSES = ['live', 'finished', 'cancelled'] as const;

function isFixtureLocked(fixture: DayFixture | null): boolean {
  if (!fixture) return false;
  return (FIXTURE_LOCKED_STATUSES as readonly string[]).includes(fixture.status);
}

function getCardOverlayText(fixtureStatus: string | null | undefined): string | null {
  switch (fixtureStatus) {
    case 'live':
      return 'Live';
    case 'finished':
      return 'Final';
    case 'postponed':
      return 'Postponed';
    case 'cancelled':
      return 'Canceled';
    default:
      return null;
  }
}

function showsLockIcon(fixtureStatus: string | null | undefined): boolean {
  return fixtureStatus === 'live' || fixtureStatus === 'finished' || fixtureStatus === 'cancelled';
}

const GOLD_FRAME = '/shapes/highlighted-3.svg';

/**
 * Format a stat value according to the sport's ratingFormat from DailyRevealSportConfig.
 *
 * - decimal3 (MLB): 0.288 → ".288"  (strip leading zero, 3 decimal places)
 * - decimal1 (NBA): 12.4  → "12.4"
 * - integer  (NFL): 87    → "87"
 * - Unknown sport or missing config: return raw string as-is.
 */
function formatStatValue(value: string, sport: string): string {
  const raw = value.trim();
  if (!raw) return '';

  const config = getDailyRevealConfig(sport);
  if (!config) return raw;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return raw;

  switch (config.ratingFormat) {
    case 'decimal3':
      // MLB batting average: 0.288 → ".288"
      return parsed.toFixed(3).replace(/^0/, '');
    case 'decimal1':
      // NBA PPG: 12.4
      return parsed.toFixed(1);
    case 'integer':
      // NFL TDs: 87
      return String(Math.round(parsed));
    default:
      return raw;
  }
}

function getMatchupLabel(fixture: DayFixture, scale: number): ReactNode {
  const home = toTeamAbbreviation(fixture.homeTeam);
  const away = toTeamAbbreviation(fixture.awayTeam);
  return (
    <>
      {home}{' '}
      <span
        style={{
          fontSize: `${8 * scale}px`,
          fontStyle: 'regular',
          fontWeight: '400',
          opacity: 0.7,
        }}
      >
        vs
      </span>{' '}
      {away}
    </>
  );
}

/**
 * Which side of the fixture the card's player is NOT on.
 *
 * The team external id is the reliable signal — it is the same Sportradar id on
 * both the card and the fixture. The abbreviation comparison is only a fallback
 * for cards synced without a team id. When neither identifies the player's side,
 * return null rather than guessing: naming the player's own club as the opponent
 * ("A.J. Brown, NE ... vs NE") is worse than showing the kickoff time alone.
 */
function getOpponentAbbreviation(
  cardTeam: string,
  cardTeamExternalId: string | null | undefined,
  fixture: DayFixture
): string | null {
  const home = toTeamAbbreviation(fixture.homeTeam);
  const away = toTeamAbbreviation(fixture.awayTeam);

  if (cardTeamExternalId) {
    if (fixture.homeTeamId === cardTeamExternalId) return away || null;
    if (fixture.awayTeamId === cardTeamExternalId) return home || null;
  }

  const team = (cardTeam || '').toUpperCase();
  if (team && team === home) return away || null;
  if (team && team === away) return home || null;

  return null;
}

/** Prop-card footer: "vs DET 1:00pm ET" from fixture + card team. */
function getPropMatchupLine(
  cardTeam: string,
  cardTeamExternalId: string | null | undefined,
  fixture: DayFixture | null | undefined,
  propGameStartAt?: string | null
): string | null {
  const timeFromFixture = fixture ? formatFixtureTime(fixture) : null;
  const timeFromProp = (() => {
    if (!propGameStartAt) return null;
    try {
      return new Date(propGameStartAt)
        .toLocaleTimeString('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric',
          minute: '2-digit',
        })
        .replace(/\s?(AM|PM)$/i, (_, m: string) => m.toLowerCase());
    } catch {
      return null;
    }
  })();
  const timeLabel = timeFromFixture ?? timeFromProp;

  if (fixture) {
    const opponent = getOpponentAbbreviation(cardTeam, cardTeamExternalId, fixture);
    if (opponent && timeLabel) return `vs ${opponent} ${timeLabel} ET`;
    if (opponent) return `vs ${opponent}`;
  }

  return timeLabel ? `${timeLabel} ET` : null;
}

function PlayerCard({
  card,
  size = 'large',
  state = 'selected',
  showGoldFrame = false,
  selectionLocked = false,
  leagueIpEnabled = false,
  isFixtureLocked = false,
  fixtureStatus,
  fixture,
  isPropBased = false,
}: {
  card: RevealCard;
  size?: 'large' | 'small';
  state?: 'selected' | 'queued';
  /** When true, show gold outline. No selection: center gets gold. Has selection: only selected card gets gold. */
  showGoldFrame?: boolean;
  /** When true, non-selected cards always use neutral frame (used on return/edit state). */
  selectionLocked?: boolean;
  /** Contest `league_ip`: headshot + desaturated team logo behind player on select view. */
  leagueIpEnabled?: boolean;
  /** When true, card's fixture has started - show locked visual treatment. */
  isFixtureLocked?: boolean;
  /** Fixture status for badge display (e.g. 'postponed', 'cancelled'). */
  fixtureStatus?: string | null;
  /** The fixture associated with this card's team, used for matchup and time display. */
  fixture?: DayFixture | null;
  /** NFL prop-based: show prop line/odds instead of matchup-first layout. */
  isPropBased?: boolean;
}) {
  const tierFrameMap: Record<string, string> = {
    allstar: '/shapes/highlighted-3.svg',
    // Keep non-selected cards on the neutral frame.
    // Gold outline should indicate selected card only.
    gold: '/shapes/highlighted-3.svg',
    silver: '/shapes/highlighted-3.svg',
    bronze: '/shapes/highlighted-3.svg',
  };
  const frameAsset = showGoldFrame
    ? GOLD_FRAME
    : selectionLocked
      ? '/shapes/true-3.svg'
      : state === 'selected'
        ? (tierFrameMap[card.tier] ?? '/shapes/highlighted-3.svg')
        : '/shapes/true-3.svg';
  const overlayAsset = '/shapes/Overlay.svg';
  const isQueued = state === 'queued' && !showGoldFrame;
  const isDimmed = isQueued || isFixtureLocked;

  const isLarge = size === 'large';
  const cardW = isLarge ? 'w-[205px]' : 'w-[150px]';
  const cardH = isLarge ? 'h-[301px]' : 'h-[220px]';
  const scale = isLarge ? 1 : 0.73;

  const overlayText = getCardOverlayText(fixtureStatus);
  const showOverlay = !!overlayText;

  // Belt-and-braces: never render a stored headshot when league IP is off.
  // The position is required for `jerseys-position` (the NFL default) to reach the
  // player's own jersey art instead of falling back to the plain team one.
  const displayImageUrl = leagueIpEnabled
    ? card.playerImage
    : getJerseyAvatarUrl(card.team, card.sport, card.position);
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => {
    setPhotoFailed(false);
  }, [displayImageUrl, card.id]);
  const hasPhoto = !!displayImageUrl && !photoFailed;
  const sportConfig = getDailyRevealConfig(card.sport);
  const formattedBattingAverage = formatStatValue(card.battingAverage, card.sport);

  const showPropLayout = isPropBased && !!card.prop;
  const playerNameLine =
    card.firstName && card.lastName
      ? null
      : (card.displayName || `${card.firstName} ${card.lastName}`.trim()).toUpperCase();

  // Stat-hit mode: matchup + kickoff at top of card
  const opponentLabel =
    !showPropLayout && fixture
      ? getMatchupLabel(fixture, scale)
      : !showPropLayout
        ? card.hit
        : null;
  const timeLabel = !showPropLayout && fixture ? formatFixtureTime(fixture) : null;
  const propMatchupLine = showPropLayout
    ? getPropMatchupLine(card.team, card.teamExternalId, fixture, card.prop?.gameStartAt)
    : null;

  return (
    <div
      className={`relative ${cardW} ${cardH} shrink-0`}
      style={
        isDimmed
          ? { filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.68))' }
          : { filter: 'drop-shadow(0 14px 26px rgba(0,0,0,0.56))' }
      }
    >
      <img src={frameAsset} alt="" className="absolute inset-0 z-0 h-full w-full object-fill" />
      {isDimmed && (
        <img
          src={overlayAsset}
          alt=""
          className="absolute inset-0 z-[2] h-full w-full object-fill opacity-90"
        />
      )}

      {/* Fixture status center overlay (live/finished/postponed/cancelled) */}
      {showOverlay && isLarge && (
        <div className="absolute inset-0 z-[3] flex items-center justify-center">
          <div className="flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5">
            {showsLockIcon(fixtureStatus) && <Lock size={14} className="text-white/80" />}
            <span className="text-[11px] font-semibold uppercase tracking-wide text-white/80">
              {overlayText}
            </span>
          </div>
        </div>
      )}

      {showPropLayout && card.prop ? (
        /* Prop card: green headline → jersey fills middle → footer (keep bottom as-is) */
        <div
          className="absolute z-[1] flex flex-col overflow-hidden"
          style={{
            top: isLarge ? 8 : 6,
            left: isLarge ? 12 : 9,
            right: isLarge ? 12 : 9,
            bottom: isLarge ? 34 : 26,
            borderTopLeftRadius: isLarge ? 16 : 12,
            borderTopRightRadius: isLarge ? 16 : 12,
          }}
        >
          <PropCardInfo
            prop={card.prop}
            scale={scale}
            isLarge={isLarge}
            isDimmed={isDimmed}
            variant="banner"
          />

          <div className="relative min-h-0 flex-1 overflow-hidden">
            {leagueIpEnabled && card.team ? (
              <MlbTeamMarkImage
                teamCode={card.team}
                sport={card.sport}
                className="pointer-events-none absolute inset-0 z-0 h-full w-full object-contain opacity-[0.28]"
                style={{
                  filter: 'saturate(0)',
                  transform: 'scale(1.15)',
                  objectPosition: 'center center',
                }}
              />
            ) : null}
            {hasPhoto ? (
              <img
                src={displayImageUrl}
                alt=""
                className={`relative z-[1] h-full w-full object-contain ${isDimmed ? 'grayscale-[20%] brightness-75' : ''}`}
                style={{ objectPosition: 'center center' }}
                onError={() => setPhotoFailed(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span
                  className={`select-none uppercase ${isDimmed ? 'text-white/10' : 'text-white/20'}`}
                  style={{
                    fontFamily: '"Druk Text Wide Trial", "Arial Black", sans-serif',
                    fontSize: `${28 * scale}px`,
                    fontWeight: 900,
                  }}
                >
                  {card.position || card.team || '?'}
                </span>
              </div>
            )}
          </div>

          <div
            className="flex shrink-0 flex-col items-center text-center"
            style={{
              paddingTop: isLarge ? 8 : 5,
              paddingBottom: isLarge ? 10 : 7,
              paddingLeft: 8,
              paddingRight: 8,
            }}
          >
            <p
              className={`uppercase leading-none ${isDimmed ? 'text-white/55' : 'text-white'}`}
              style={{
                fontFamily: '"Saira Semi Condensed", sans-serif',
                fontSize: `${isLarge ? 10 : 7.5}px`,
                fontWeight: 600,
                letterSpacing: '0.06em',
              }}
            >
              {card.firstName || playerNameLine?.split(/\s+/)[0] || ''}
            </p>
            <FitText
              text={
                card.lastName ||
                playerNameLine?.split(/\s+/).slice(1).join(' ') ||
                card.displayName.toUpperCase()
              }
              maxFontSize={isLarge ? 15 : 11}
              minFontSize={isLarge ? 9 : 7}
              className={`mt-[2px] w-full text-center uppercase leading-none ${isDimmed ? 'text-white/70' : 'text-white'}`}
              style={{
                fontFamily: '"Druk Text Wide Trial", "Arial Black", sans-serif',
                fontWeight: 900,
              }}
            />
            <p
              className={`mt-[5px] uppercase leading-none ${isDimmed ? 'text-white/40' : 'text-[#C0C0C0]'}`}
              style={{
                fontFamily: '"Saira Semi Condensed", sans-serif',
                fontSize: `${isLarge ? 10.5 : 8}px`,
                fontWeight: 700,
                letterSpacing: '0.03em',
              }}
            >
              {[card.team, card.number, card.position].filter(Boolean).join(' · ')}
            </p>
            {propMatchupLine ? (
              <p
                className={`mt-[4px] leading-none ${isDimmed ? 'text-white/30' : 'text-[#8A8A8A]'}`}
                style={{
                  fontFamily: '"Saira Semi Condensed", sans-serif',
                  fontSize: `${isLarge ? 9.5 : 7}px`,
                  fontWeight: 600,
                }}
              >
                {propMatchupLine}
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="relative z-[1] flex h-full w-full flex-col items-center overflow-hidden px-3 pt-[18px] pb-[8px]">
          {(opponentLabel || timeLabel) && (
            <div
              className={`flex w-full shrink-0 items-baseline justify-center gap-1 ${isDimmed ? 'text-[#8f939a]' : 'text-white'}`}
            >
              {opponentLabel && (
                <span
                  className="whitespace-nowrap uppercase leading-tight"
                  style={{
                    fontFamily: '"Saira Semi Condensed", sans-serif',
                    fontSize: `${12 * scale}px`,
                    fontWeight: 700,
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                >
                  {opponentLabel}
                </span>
              )}
              {timeLabel && (
                <>
                  {opponentLabel && (
                    <span
                      className="whitespace-nowrap"
                      style={{
                        fontSize: `${12 * scale}px`,
                        fontFamily: '"Saira Semi Condensed", sans-serif',
                        fontWeight: 400,
                        opacity: 0.7,
                      }}
                    >
                      &bull;
                    </span>
                  )}
                  <span
                    className="whitespace-nowrap"
                    style={{
                      fontSize: `${10.5 * scale}px`,
                      fontFamily: '"Saira Semi Condensed", sans-serif',
                      fontWeight: 700,
                    }}
                  >
                    {timeLabel}
                  </span>
                </>
              )}
            </div>
          )}

          <div
            className={`relative mt-[2px] shrink-0 overflow-hidden rounded-[12px] bg-[#0a0c0a] ${
              isLarge ? 'h-[152px] w-[163px]' : 'h-[114px] w-[120px]'
            }`}
          >
            {leagueIpEnabled && card.team ? (
              <MlbTeamMarkImage
                teamCode={card.team}
                sport={card.sport}
                className="pointer-events-none absolute inset-0 z-0 h-full w-full object-contain opacity-[0.38]"
                style={{
                  filter: 'saturate(0)',
                  transform: 'scale(1.2)',
                  objectPosition: 'center center',
                }}
              />
            ) : null}
            {hasPhoto ? (
              <img
                src={displayImageUrl}
                alt=""
                className={`relative z-[1] block h-full w-full object-cover ${isDimmed ? 'grayscale-[20%] brightness-75' : ''}`}
                style={{ objectPosition: 'center 15%' }}
                onError={() => setPhotoFailed(true)}
              />
            ) : (
              <div
                className={`flex h-full w-full items-center justify-center text-center px-2 ${isDimmed ? 'text-white/20' : 'text-white/40'}`}
                style={{
                  fontFamily: '"Saira Semi Condensed", sans-serif',
                  fontSize: `${11 * scale}px`,
                  fontWeight: 700,
                }}
              >
                {card.displayName || `${card.firstName} ${card.lastName}`.trim()}
              </div>
            )}
          </div>

          <div className="mt-[8px] flex w-full shrink-0 flex-col items-center">
            {playerNameLine ? (
              <p
                className={`text-center uppercase leading-tight ${isDimmed ? 'text-[#b6b9bf]' : 'text-[#ffffff]'}`}
                style={{
                  fontFamily: '"Druk Text Wide Trial", "Arial Black", sans-serif',
                  fontSize: `${10.5 * scale}px`,
                  fontWeight: 900,
                  fontFeatureSettings: "'liga' off, 'clig' off",
                }}
              >
                {playerNameLine}
              </p>
            ) : (
              <>
                <p
                  className={`text-center uppercase leading-none ${isDimmed ? 'text-[#a1a4aa]' : 'text-[#ffffff]'}`}
                  style={{
                    fontFamily: '"Druk Wide Trial", "Arial Black", sans-serif',
                    fontSize: `${8 * scale}px`,
                    fontWeight: 500,
                    letterSpacing: `${0.3 * scale}px`,
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                >
                  {card.firstName}
                </p>
                <FitText
                  text={card.lastName || card.displayName.toUpperCase()}
                  maxFontSize={13.19 * scale}
                  minFontSize={8 * scale}
                  className={`mt-[1px] w-full text-center uppercase leading-tight ${isDimmed ? 'text-[#b6b9bf]' : 'text-[#ffffff]'}`}
                  style={{
                    fontFamily: '"Druk Text Wide Trial", "Arial Black", sans-serif',
                    fontWeight: 900,
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                />
              </>
            )}

            <div className="mt-[4px] flex items-center gap-[4px]">
              {leagueIpEnabled && card.team ? (
                <div
                  className={`${isLarge ? 'h-[16px] w-[16px]' : 'h-[12px] w-[12px]'} flex shrink-0 items-center justify-center overflow-hidden rounded-[3px] border ${
                    isDimmed ? 'border-white/20' : 'border-white/30'
                  }`}
                >
                  <MlbTeamLogoImage
                    teamCode={card.team}
                    sport={card.sport}
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : null}
              <span
                className={`text-center ${isDimmed ? 'text-[#8f939a]' : 'text-[#BCBCBC]'}`}
                style={{
                  fontFamily: '"Saira Semi Condensed", sans-serif',
                  fontSize: `${12.61 * scale}px`,
                  fontWeight: 700,
                  lineHeight: `${10.088 * scale}px`,
                  fontFeatureSettings: "'liga' off, 'clig' off",
                }}
              >
                {card.team}
                {card.number ? ` • ${card.number}` : ''}
                {card.position ? ` • ${card.position}` : ''}
              </span>
            </div>

            {formattedBattingAverage && (
              <p
                className={`mt-[3px] text-center ${isDimmed ? 'text-[#7f838b]' : 'text-[#BCBCBC]'}`}
                style={{
                  fontFamily: '"Saira Semi Condensed", sans-serif',
                  fontSize: `${10 * scale}px`,
                  fontWeight: 600,
                  lineHeight: `${10.088 * scale}px`,
                  fontFeatureSettings: "'liga' off, 'clig' off",
                }}
              >
                {sportConfig?.ratingLabel ?? 'AVG'} {formattedBattingAverage}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type CardSelectViewProps = {
  cards: RevealCard[];
  dayId: string;
  /** Contest the pick belongs to — carried back so the entry view opens on it. */
  gameId?: string | null;
  /** Where to go once the pick is committed; a `/game/:gameId` path when known. */
  returnTo?: string;
  fixtures?: DayFixture[];
  currentPickLocked?: boolean;
  onCardPicked?: (card: RevealCard) => void;
  leagueIpEnabled?: boolean;
  isPropBased?: boolean;
  categoryName?: string | null;
};

const CARD_FLIP_SOUND = '/animationv2/Flipping%20Poker%20Card%20v1.mp3';

export default function CardSelectView({
  cards,
  dayId,
  gameId = null,
  returnTo = '/',
  fixtures = [],
  currentPickLocked = false,
  onCardPicked,
  leagueIpEnabled = false,
  isPropBased = false,
  categoryName,
}: CardSelectViewProps) {
  const { isMuted } = useAudioSettings();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cardFlipAudioRef = useRef<HTMLAudioElement | null>(null);
  const hasExistingSelection = cards.some(c => c.isSelected);
  const selectedIndex = cards.findIndex(c => c.isSelected);
  const [activeIndex, setActiveIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

  const getCardFixture = (card: RevealCard) =>
    findFixtureForCard(card.teamExternalId, fixtures, card.team);
  const isCardLocked = (card: RevealCard) => {
    const fixture = getCardFixture(card);
    if (isPropBased) {
      return isPropPickLocked(card.prop, Date.now(), fixture);
    }
    return isFixtureLocked(fixture);
  };

  const getShowGoldFrame = (card: RevealCard, state: 'selected' | 'queued') =>
    hasExistingSelection ? card.isSelected : state === 'selected';
  const [hasNavigated, setHasNavigated] = useState(false);
  const [transition, setTransition] = useState<{
    direction: 'next' | 'prev';
    phase: 'start' | 'run';
    fromIndex: number;
    toIndex: number;
    fromHasNavigated: boolean;
    toHasNavigated: boolean;
  } | null>(null);
  const touchStartX = useRef<number | null>(null);
  const transitionTimeoutRef = useRef<number | null>(null);
  const TRANSITION_MS = 360;

  if (cards.length === 0) {
    return null;
  }

  const getIndex = (base: number, offset: number) => (base + offset + cards.length) % cards.length;

  // Selecting commits straight away, first pick included. Prop-based (NFL) cards
  // used to raise a Confirm Pick dialog on the first selection only, which put an
  // extra step between the player and a pick they had already chosen — and one that
  // no other Daily Reveal card had. The pick stays editable until cutoff either way.
  const handleSelectCard = async () => {
    await submitPick(cards[activeIndex]);
  };

  const submitPick = async (selected: RevealCard) => {
    try {
      if (onCardPicked) {
        await onCardPicked(selected);
      }
      // Only cache the pick once the server has accepted it (DKPLAT-724). Writing it
      // first meant a failed request left a stand-in nothing ever removed, so the home
      // screen showed a confident "TODAY'S PICK … ✏️ EDIT" for a selection the backend
      // had never recorded — indistinguishable from a real one, so nobody retried.
      setStoredTodaysPick(selected, dayId);

      await queryClient.invalidateQueries({ queryKey: dailyRevealKeys.entry(dayId) });
      await queryClient.invalidateQueries({ queryKey: dailyRevealKeys.games });
      await queryClient.invalidateQueries({ queryKey: ['daily-reveal', 'game'] });
      // Back to the contest the pick was made in, so the player sees the selection
      // they just made on the week they were playing (DKPLAT-763).
      navigate(returnTo, { state: { dayId, gameId } });
    } catch {
      // Belt and braces: also drop any stand-in left by an earlier attempt, so a
      // failed retry cannot resurrect a pick that is not on the server.
      clearStoredTodaysPick();
      // Parent shows toast; stay on picker
    }
  };

  const triggerTransition = (direction: 'next' | 'prev') => {
    if (transition) return;

    // Play card flip sound on switch
    if (!cardFlipAudioRef.current) {
      cardFlipAudioRef.current = new Audio(CARD_FLIP_SOUND);
    }
    cardFlipAudioRef.current.muted = isMuted;
    cardFlipAudioRef.current.currentTime = 0;
    cardFlipAudioRef.current.play().catch(() => {});

    const fromIndex = activeIndex;
    const toIndex = direction === 'next' ? getIndex(fromIndex, 1) : getIndex(fromIndex, -1);
    const toHasNavigated = true;

    setTransition({
      direction,
      phase: 'start',
      fromIndex,
      toIndex,
      fromHasNavigated: hasNavigated,
      toHasNavigated,
    });

    window.requestAnimationFrame(() => {
      setTransition(prev => (prev ? { ...prev, phase: 'run' } : null));
    });

    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
    }
    transitionTimeoutRef.current = window.setTimeout(() => {
      setActiveIndex(toIndex);
      setHasNavigated(toHasNavigated);
      setTransition(null);
      transitionTimeoutRef.current = null;
    }, TRANSITION_MS + 20);
  };

  const goPrev = () => {
    triggerTransition('prev');
  };
  const goNext = () => {
    triggerTransition('next');
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    if (Math.abs(dx) > 35) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  const activeCard = cards[activeIndex];
  const prevCard = cards[getIndex(activeIndex, -1)];
  const nextCard = cards[getIndex(activeIndex, 1)];

  const activeCardLocked = isCardLocked(activeCard);
  const isSelectDisabled = activeCardLocked || currentPickLocked;

  const selectButtonLabel = isPropBased ? 'Select Prop' : 'Select Player';
  const selectHint = isPropBased
    ? 'Select (1) player prop to hit the line'
    : 'Select (1) player to score 1+ Hit';

  const transitionStyle = {
    transition: `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity ${TRANSITION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
    willChange: 'transform, opacity',
  } as const;

  const renderStaticCarousel = () => (
    <>
      {hasNavigated && (
        <div
          className="absolute top-1/2 z-[1] opacity-95"
          style={{ left: 'calc(50% - 145px)', transform: 'translate(-50%, -50%)' }}
        >
          <PlayerCard
            leagueIpEnabled={leagueIpEnabled}
            card={prevCard}
            size="small"
            state="queued"
            showGoldFrame={getShowGoldFrame(prevCard, 'queued')}
            selectionLocked={hasExistingSelection}
            isFixtureLocked={isCardLocked(prevCard)}
            fixtureStatus={getCardFixture(prevCard)?.status}
            fixture={getCardFixture(prevCard)}
            isPropBased={isPropBased}
          />
        </div>
      )}

      <div
        className="absolute top-1/2 z-[1] opacity-95"
        style={{ left: 'calc(50% + 145px)', transform: 'translate(-50%, -50%)' }}
      >
        <PlayerCard
          leagueIpEnabled={leagueIpEnabled}
          card={nextCard}
          size="small"
          state="queued"
          showGoldFrame={getShowGoldFrame(nextCard, 'queued')}
          selectionLocked={hasExistingSelection}
          isFixtureLocked={isCardLocked(nextCard)}
          fixtureStatus={getCardFixture(nextCard)?.status}
          fixture={getCardFixture(nextCard)}
          isPropBased={isPropBased}
        />
      </div>

      <div
        className="relative z-[2]"
        style={{ filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.58))' }}
      >
        <PlayerCard
          leagueIpEnabled={leagueIpEnabled}
          card={activeCard}
          size="large"
          state="selected"
          showGoldFrame={getShowGoldFrame(activeCard, 'selected')}
          selectionLocked={hasExistingSelection}
          isFixtureLocked={isCardLocked(activeCard)}
          fixtureStatus={getCardFixture(activeCard)?.status}
          fixture={getCardFixture(activeCard)}
          isPropBased={isPropBased}
        />
      </div>
    </>
  );

  const renderAnimatedCarousel = () => {
    if (!transition) return null;

    const isNext = transition.direction === 'next';
    const run = transition.phase === 'run';
    const fromPrev = cards[getIndex(transition.fromIndex, -1)];
    const fromNext = cards[getIndex(transition.fromIndex, 1)];
    const toPrev = cards[getIndex(transition.toIndex, -1)];
    const toNext = cards[getIndex(transition.toIndex, 1)];
    const outgoingCenter = cards[transition.fromIndex];
    const incomingCenter = cards[transition.toIndex];

    return (
      <>
        {transition.fromHasNavigated && isNext && (
          <div
            className="absolute top-1/2 z-[1]"
            style={{
              left: '50%',
              opacity: run ? 0 : 0.95,
              transform: run
                ? 'translate(calc(-50% - 290px), -50%)'
                : 'translate(calc(-50% - 145px), -50%)',
              ...transitionStyle,
            }}
          >
            <PlayerCard
              leagueIpEnabled={leagueIpEnabled}
              card={fromPrev}
              size="small"
              state="queued"
              showGoldFrame={getShowGoldFrame(fromPrev, 'queued')}
              selectionLocked={hasExistingSelection}
              fixture={getCardFixture(fromPrev)}
              isPropBased={isPropBased}
            />
          </div>
        )}

        {isNext && (
          <div
            className="absolute top-1/2 z-[1]"
            style={{
              left: '50%',
              opacity: run ? 0.95 : 0,
              transform: run
                ? 'translate(calc(-50% + 145px), -50%)'
                : 'translate(calc(-50% + 290px), -50%)',
              ...transitionStyle,
            }}
          >
            <PlayerCard
              leagueIpEnabled={leagueIpEnabled}
              card={toNext}
              size="small"
              state="queued"
              showGoldFrame={getShowGoldFrame(toNext, 'queued')}
              selectionLocked={hasExistingSelection}
              fixture={getCardFixture(toNext)}
              isPropBased={isPropBased}
            />
          </div>
        )}

        {transition.fromHasNavigated && !isNext && (
          <div
            className="absolute top-1/2 z-[1]"
            style={{
              left: '50%',
              opacity: run ? 0 : 0.95,
              transform: run
                ? 'translate(calc(-50% + 290px), -50%)'
                : 'translate(calc(-50% + 145px), -50%)',
              ...transitionStyle,
            }}
          >
            <PlayerCard
              leagueIpEnabled={leagueIpEnabled}
              card={fromNext}
              size="small"
              state="queued"
              showGoldFrame={getShowGoldFrame(fromNext, 'queued')}
              selectionLocked={hasExistingSelection}
              fixture={getCardFixture(fromNext)}
              isPropBased={isPropBased}
            />
          </div>
        )}

        {!isNext && (
          <div
            className="absolute top-1/2 z-[1]"
            style={{
              left: '50%',
              opacity: run ? 0.95 : 0,
              transform: run
                ? 'translate(calc(-50% - 145px), -50%)'
                : 'translate(calc(-50% - 290px), -50%)',
              ...transitionStyle,
            }}
          >
            <PlayerCard
              leagueIpEnabled={leagueIpEnabled}
              card={toPrev}
              size="small"
              state="queued"
              showGoldFrame={getShowGoldFrame(toPrev, 'queued')}
              selectionLocked={hasExistingSelection}
              fixture={getCardFixture(toPrev)}
              isPropBased={isPropBased}
            />
          </div>
        )}

        <div
          className="absolute top-1/2 z-[2]"
          style={{
            left: '50%',
            opacity: run ? 0.95 : 1,
            transform: isNext
              ? run
                ? 'translate(calc(-50% - 145px), -50%) scale(0.73)'
                : 'translate(-50%, -50%) scale(1)'
              : run
                ? 'translate(calc(-50% + 145px), -50%) scale(0.73)'
                : 'translate(-50%, -50%) scale(1)',
            ...transitionStyle,
            filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.58))',
          }}
        >
          <PlayerCard
            leagueIpEnabled={leagueIpEnabled}
            card={outgoingCenter}
            size="large"
            state="selected"
            showGoldFrame={getShowGoldFrame(outgoingCenter, 'selected')}
            selectionLocked={hasExistingSelection}
            fixture={getCardFixture(outgoingCenter)}
            isPropBased={isPropBased}
          />
        </div>

        <div
          className="absolute top-1/2 z-[3]"
          style={{
            left: '50%',
            opacity: run ? 1 : 0.95,
            transform: isNext
              ? run
                ? 'translate(-50%, -50%) scale(1)'
                : 'translate(calc(-50% + 145px), -50%) scale(0.73)'
              : run
                ? 'translate(-50%, -50%) scale(1)'
                : 'translate(calc(-50% - 145px), -50%) scale(0.73)',
            ...transitionStyle,
            filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.58))',
          }}
        >
          <PlayerCard
            leagueIpEnabled={leagueIpEnabled}
            card={incomingCenter}
            size="large"
            state="selected"
            showGoldFrame={getShowGoldFrame(incomingCenter, 'selected')}
            selectionLocked={hasExistingSelection}
            fixture={getCardFixture(incomingCenter)}
            isPropBased={isPropBased}
          />
        </div>
      </>
    );
  };

  return (
    <div className="daily-reveal-card-select-fade flex w-full flex-1 flex-col items-center">
      <p className="daily-reveal-heading mt-[19px] text-[13px] uppercase tracking-[0.2px]">
        {new Date().toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })}
      </p>
      <h1 className="daily-reveal-heading mt-[6px] text-[24.611px] uppercase leading-none tracking-[0.2px]">
        {isPropBased && categoryName ? categoryName : 'Your Pick'}
      </h1>
      <p className="mt-[16px] text-center text-[12px] font-medium text-[#8a8a8a]">{selectHint}</p>

      <div
        className="fixed left-1/2 top-1/2 z-30 flex w-[375px] max-w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center"
        style={{ height: '334px' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {transition ? renderAnimatedCarousel() : renderStaticCarousel()}
      </div>

      <div
        className="fixed left-1/2 z-30 flex w-full -translate-x-1/2 items-center justify-center gap-8"
        style={{ top: 'calc(50% + 200px)' }}
      >
        <button
          type="button"
          aria-label="Previous card"
          onClick={goPrev}
          disabled={!!transition}
          className="text-white disabled:opacity-45"
        >
          <ChevronLeft size={32} strokeWidth={2.5} />
        </button>

        {isSelectDisabled ? (
          <div className="relative h-[42px] w-[157px] shrink-0 rounded-[6px] bg-[#2a2a2a]">
            <span
              className="inline-flex h-full w-full items-center justify-center uppercase text-[#4a4a4a]"
              style={{
                fontFamily: '"Druk Text Wide Trial", "Arial Black", sans-serif',
                fontSize: '11px',
                fontWeight: 900,
                letterSpacing: '0.5px',
              }}
            >
              {selectButtonLabel}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleSelectCard}
            className="daily-reveal-cta-button relative h-[42px] w-[157px] shrink-0"
          >
            <img
              src="/shapes/select-card-cta.svg"
              alt=""
              aria-hidden
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
            <span className="daily-reveal-button-primary relative z-10 inline-flex h-full w-full items-center justify-center uppercase">
              {selectButtonLabel}
            </span>
          </button>
        )}

        <button
          type="button"
          aria-label="Next card"
          onClick={goNext}
          disabled={!!transition}
          className="text-white disabled:opacity-45"
        >
          <ChevronRight size={32} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
